// src/components/PurposePrinciplesTimeline.jsx
import React from "react";

const DEFAULT_PALETTE = {
  primary: "#2E6F40", // dots / strong accents
  mint: "#CFFFDC",    // background
  mid: "#68BA7F",     // dashed rail + ornaments
  ink: "#253D2C",     // text
};

export default function PurposePrinciplesTimeline({
  title = "Why Clients Choose Rex Counsel",
  intro = "We align law, technology, and human rights across India, the UK, and the US—turning complex, cross-border issues into clear, actionable strategy.",
  items = [],
  purposeSteps = [],
  principles = [],
  showOrnaments = false,
  palette = DEFAULT_PALETTE,
}) {
  const COLORS = { ...DEFAULT_PALETTE, ...palette };

  // normalize inputs
  const toArt = (icon, art) => {
    if (art) return art;
    switch (icon) {
      case "🧭":
      case "🔎": return "compass";
      case "📄": return "document";
      case "⚡": return "bolt";
      case "🌍":
      case "🌐": return "globe";
      case "💬": return "chat";
      case "⚖️":
      case "🔨": return "gavel";
      default: return undefined;
    }
  };
  const toRows = (arr=[]) => (Array.isArray(arr) ? arr : []).map(r => ({
    h: r.h || r.title || r.name || "",
    p: r.p || r.text || r.desc || r.description || "",
    art: toArt(r.icon, r.art),
  }));

  const rows = [
    ...toRows(items),
    ...toRows(purposeSteps),
    ...toRows(principles),
  ];
  const content = rows.length ? rows : [
    { h: "We believe in clarity before action", p: "We map facts to forum and law—what must be proven, where, and by when.", art: "compass" },
    { h: "We plan like strategists, draft like litigators", p: "Every word has a purpose. We prepare submissions that travel across jurisdictions.", art: "document" },
    { h: "We value drive and professionalism", p: "Speed with discipline: crisp arguments, realistic reliefs, organised case management.", art: "gavel" },
  ];

  return (
    <section
      className="relative min-h-screen"
      style={{
        background: `linear-gradient(180deg, ${COLORS.mint} 0%, rgba(207,255,220,0.75) 35%, rgba(207,255,220,0.35) 65%, #FFFFFF 100%)`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-10 md:mb-12">
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight leading-tight" style={{ color: COLORS.ink }}>
            {title}
          </h2>
          <p className="mt-4 max-w-4xl text-base md:text-lg leading-relaxed" style={{ color: COLORS.ink }}>
            {intro}
          </p>
        </header>

        <div className="relative">
          {/* left dashed rail */}
          <div
            className="absolute left-6 md:left-8 top-0 bottom-0 border-l-2 border-dashed"
            style={{ borderColor: COLORS.mid }}
          />
          <ol className="space-y-10 md:space-y-12">
            {content.map((item, i) => (
              <li key={i} className="relative pl-16 md:pl-24">
                {/* dot */}
                <span
                  className="absolute left-6 md:left-8 -translate-x-1/2 top-1.5 w-4 h-4 rounded-full"
                  style={{
                    background: COLORS.primary,
                    boxShadow: `0 0 0 10px ${COLORS.mint}`,
                  }}
                />
                {/* optional faint ornament */}
                {showOrnaments && item.art && (
                  <div className="pointer-events-none absolute -top-6 md:-top-8 left-0 md:left-8 opacity-10">
                    <Ornament kind={item.art} color={COLORS.mid} />
                  </div>
                )}
                <div className="relative z-10">
                  <h3 className="text-2xl md:text-3xl font-semibold tracking-tight mb-2" style={{ color: COLORS.ink }}>
                    {item.h}
                  </h3>
                  <p className="text-sm md:text-base leading-relaxed" style={{ color: COLORS.ink }}>
                    {item.p}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

function Ornament({ kind = "gavel", color = DEFAULT_PALETTE.mid }) {
  return (
    <svg viewBox="0 0 400 400" className="w-48 h-48 md:w-56 md:h-56">
      <g fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        {kind === "gavel" && (<>
          <rect x="120" y="140" width="100" height="30" rx="6" transform="rotate(-30 170 155)" />
          <rect x="190" y="100" width="80" height="40" rx="8" />
          <line x1="150" y1="200" x2="280" y2="200" strokeWidth="6" />
          <rect x="240" y="195" width="70" height="10" rx="5" />
        </>)}
        {kind === "globe" && (<>
          <circle cx="200" cy="200" r="80" strokeWidth="3" />
          <ellipse cx="200" cy="200" rx="80" ry="35" />
          <line x1="120" y1="200" x2="280" y2="200" />
          <line x1="200" y1="120" x2="200" y2="280" />
        </>)}
        {kind === "compass" && (<>
          <circle cx="200" cy="200" r="70" strokeWidth="3" />
          <path d="M 200 130 L 215 200 L 200 270 L 185 200 Z" fill={color} fillOpacity="0.12" />
          <circle cx="200" cy="200" r="8" fill={DEFAULT_PALETTE.primary} />
        </>)}
        {kind === "document" && (<>
          <rect x="130" y="100" width="140" height="180" rx="12" strokeWidth="3" />
          <line x1="155" y1="140" x2="245" y2="140" />
          <line x1="155" y1="170" x2="235" y2="170" />
        </>)}
        {kind === "bolt" && (
          <path d="M 210 100 L 170 180 L 210 180 L 180 280 L 230 190 L 200 190 Z" fill={color} fillOpacity="0.12" />
        )}
        {kind === "chat" && (<>
          <rect x="120" y="130" width="160" height="110" rx="15" strokeWidth="3" />
          <path d="M 180 240 L 200 260 L 240 240" strokeWidth="3" />
        </>)}
      </g>
    </svg>
  );
}
